/**
 * Utility functions for JWT token decoding and extraction
 */

import { warn, debug } from '@/utils/logger';

export interface DecodedToken {
  email: string | null;
  cognitoUsername: string | null;
}

export interface DecodedJWTPayload {
  exp?: number;
  iat?: number;
  [key: string]: unknown;
}

/**
 * Decodes a JWT token and returns the payload
 * 
 * @param token - The JWT token string
 * @returns Decoded payload or null if decoding fails
 */
export function decodeJWT(token: string): DecodedJWTPayload | null {
  try {
    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) {
      warn('Invalid token format: expected 3 parts, got', tokenParts.length);
      return null;
    }

    const decodedPayload = JSON.parse(atob(tokenParts[1]));
    return decodedPayload;
  } catch (e) {
    warn('Could not decode JWT token:', e);
    return null;
  }
}

/**
 * Checks if a JWT token is expired
 * 
 * @param token - The JWT token string
 * @returns true if token is expired or invalid, false if valid
 */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeJWT(token);
  
  if (!decoded || !decoded.exp) {
    // If we can't decode or no exp claim, consider it expired for safety
    return true;
  }

  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = decoded.exp * 1000;
  const now = Date.now();
  const isExpired = expirationTime < now;

  if (isExpired) {
    debug('🔐 [isTokenExpired] Token is expired', {
      expirationTime: new Date(expirationTime).toISOString(),
      currentTime: new Date(now).toISOString(),
      expiredBy: now - expirationTime
    });
  }

  return isExpired;
}

/**
 * Decodes a Cognito ID token and extracts email and cognitoUsername
 * 
 * @param idToken - The JWT ID token string from Cognito
 * @returns Object with email and cognitoUsername (or null if decoding fails)
 */
export function decodeIdToken(idToken: string): DecodedToken {
  try {
    const decodedToken = decodeJWT(idToken);
    
    if (!decodedToken) {
      return { email: null, cognitoUsername: null };
    }

    // Type guard helper to safely extract string values
    const getStringValue = (value: unknown): string | null => {
      return typeof value === 'string' ? value : null;
    };

    const email: string | null = getStringValue(decodedToken.email);
    const cognitoUsername: string | null = getStringValue(decodedToken['cognito:username']) || getStringValue(decodedToken.sub);

    return {
      email,
      cognitoUsername
    };
  } catch (e) {
    warn('Could not decode ID token:', e);
    return { email: null, cognitoUsername: null };
  }
}

